import apiClient from './apiClient';
import type { ApiResponse, PaginatedResponse } from '../types/api';

export interface InvoiceItem {
    id: number;
    invoice_id: number;
    service_id: number | null;
    description: string;
    quantity: number;
    unit_price: string;
    discount: string;
    total: string;
    itbis_type: string | null;
    itbis_name: string | null;
    itbis_rate: string | null;
    itbis_amount: string | null;
    service?: { id: number; name: string; code: string };
}

export interface ConsultationInvoice {
    id: number;
    invoice_number: string;
    consultation_id: number | null;
    patient_id: number;
    doctor_id: number;
    status: 'draft' | 'paid' | 'cancelled';
    payment_method: 'cash' | 'card' | 'insurance' | 'transfer' | 'mixed' | null;
    ncf_type_id: number | null;
    ncf_number: string | null;
    ncf_sequence_id: number | null;
    issuer_rnc: string | null;
    receiver_rnc: string | null;
    fiscal_status: 'pending' | 'issued' | 'cancelled' | null;
    valid_until: string | null;
    is_fiscal: boolean;
    ecf_submission_status: 'pending' | 'submitted' | 'accepted' | 'rejected' | 'error' | null;
    provider_ref: string | null;
    ecf_rejection_reason: string | null;
    ncf_type?: { id: number; code: string; name: string } | null;
    subtotal: string;
    discount: string;
    tax: string;
    total: string;
    insurance_coverage: string;
    patient_copay: string;
    notes: string | null;
    issued_by: number | { id: number; name: string };
    paid_at: string | null;
    created_at: string;
    updated_at: string;
    patient?: { id: number; first_name: string; last_name: string; identification?: string };
    doctor?: { id: number; first_name: string; last_name: string; specialty?: { name: string } };
    consultation?: { id: number; consultation_date: string; chief_complaint: string };
    items?: InvoiceItem[];
}

export interface CreateInvoiceRequest {
    consultation_id?: number;
    patient_id?: number;
    doctor_id?: number;
    payment_method?: string;
    discount?: number;
    tax?: number;
    insurance_coverage?: number;
    ncf_type_id?: number;
    notes?: string;
    items: {
        description: string;
        quantity: number;
        unit_price: number;
        discount?: number;
        itbis_type?: string;
        itbis_amount?: number;
        service_id?: number;
    }[];
}

export interface UpdateInvoiceRequest {
    status?: 'draft' | 'paid' | 'cancelled';
    payment_method?: string;
    discount?: number;
    tax?: number;
    insurance_coverage?: number;
    notes?: string;
}

export interface InvoiceFilters {
    page?: number;
    per_page?: number;
    patient_id?: number;
    doctor_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    fiscal?: string;
    fiscal_status?: string;
}

export const invoiceService = {
    async getAll(filters: InvoiceFilters = {}): Promise<PaginatedResponse<ConsultationInvoice>> {
        const params = new URLSearchParams();
        if (filters.page) params.set('page', String(filters.page));
        params.set('per_page', String(filters.per_page || 20));
        if (filters.patient_id) params.set('patient_id', String(filters.patient_id));
        if (filters.doctor_id) params.set('doctor_id', String(filters.doctor_id));
        if (filters.status) params.set('status', filters.status);
        if (filters.date_from) params.set('date_from', filters.date_from);
        if (filters.date_to) params.set('date_to', filters.date_to);
        if (filters.search) params.set('search', filters.search);
        if (filters.fiscal) params.set('fiscal', filters.fiscal);
        if (filters.fiscal_status) params.set('fiscal_status', filters.fiscal_status);
        const response = await apiClient.get<PaginatedResponse<ConsultationInvoice>>(`/consultation-invoices?${params.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ConsultationInvoice> {
        const response = await apiClient.get<ApiResponse<ConsultationInvoice>>(`/consultation-invoices/${id}`);
        return response.data.data;
    },

    async getByConsultation(consultationId: number): Promise<ConsultationInvoice | null> {
        const response = await apiClient.get<ApiResponse<ConsultationInvoice | null>>(`/consultation-invoices/by-consultation/${consultationId}`);
        return response.data.data;
    },

    async create(data: CreateInvoiceRequest): Promise<ConsultationInvoice> {
        const response = await apiClient.post<ApiResponse<ConsultationInvoice>>('/consultation-invoices', data);
        return response.data.data;
    },

    async update(id: number, data: UpdateInvoiceRequest): Promise<ConsultationInvoice> {
        const response = await apiClient.put<ApiResponse<ConsultationInvoice>>(`/consultation-invoices/${id}`, data);
        return response.data.data;
    },

    async downloadPdf(id: number, invoiceNumber: string): Promise<void> {
        const response = await apiClient.get<ArrayBuffer>(`/consultation-invoices/${id}/pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data as BlobPart], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    },
};

export default invoiceService;
